import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FileCopy } from "@material-ui/icons";
import { toast } from "react-toastify";
import {
  listChatWidgets,
  createChatWidget,
  updateChatWidget,
} from "../../services/chatWidgetService";

const useStyles = makeStyles(() => ({
  codeBox: {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    borderRadius: 6,
    padding: "10px 14px",
    fontFamily: "monospace",
    fontSize: 12,
    wordBreak: "break-all",
    position: "relative",
    cursor: "pointer",
    lineHeight: 1.5,
  },
  copyBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    color: "#888",
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginTop: 8,
    flexWrap: "wrap",
  },
  colorBlock: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  colorSwatch: {
    width: 36,
    height: 28,
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
  },
  preview: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
}));

const DEFAULT_FORM = {
  name: "",
  title: "Fale conosco",
  welcomeMessage: "",
  buttonColor: "#0070f3",
  primaryColor: "#0070f3",
  buttonPosition: "bottom-right",
  allowedDomains: "*",
};

const ChatWidgetModal = ({ open, onClose, whatsapp, onSaved }) => {
  const classes = useStyles();
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM });

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const isEdit = !!(whatsapp && whatsapp.channel === "widget");

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      loadWidget();
    } else {
      setWidget(null);
      setForm({ ...DEFAULT_FORM });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, whatsapp]);

  const loadWidget = async () => {
    setLoading(true);
    try {
      const { data } = await listChatWidgets();
      // Coerção numérica para evitar mismatch string/number entre API e prop
      const w = data.find((x) => Number(x.whatsappId) === Number(whatsapp.id));
      if (w) {
        setWidget(w);
        setForm({
          name: w.name || "",
          title: w.title || "Fale conosco",
          welcomeMessage: w.welcomeMessage || "",
          buttonColor: w.buttonColor || "#0070f3",
          primaryColor: w.primaryColor || "#0070f3",
          buttonPosition: w.buttonPosition || "bottom-right",
          allowedDomains: w.allowedDomains || "*",
        });
      } else {
        toast.warning("Configuração do widget não encontrada. Verifique se o widget foi criado corretamente.");
      }
    } catch {
      toast.error("Erro ao carregar dados do widget.");
    } finally {
      setLoading(false);
    }
  };

  const f = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warning("Informe um nome para o widget.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit && widget) {
        await updateChatWidget(widget.id, form);
        toast.success("Widget atualizado!");
      } else {
        await createChatWidget(form);
        toast.success("Widget criado!");
      }
      if (onSaved) onSaved();
      onClose();
    } catch {
      toast.error("Erro ao salvar widget.");
    } finally {
      setSaving(false);
    }
  };

  const snippet = widget
    ? `<script src="${backendUrl}/wc/${widget.code}/embed.js"></script>`
    : null;

  const handleCopy = () => {
    if (!snippet) return;
    navigator.clipboard.writeText(snippet).then(() => toast.info("Snippet copiado!"));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Editar Widget de Chat" : "Novo Widget de Chat"}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {/* Preview da bolha */}
            <Box display="flex" alignItems="center" gap={2} mb={2} style={{ gap: 12 }}>
              <Box
                className={classes.preview}
                style={{ backgroundColor: form.buttonColor }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                </svg>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Pré-visualização da bolha de chat
              </Typography>
            </Box>

            <TextField
              label="Nome do widget *"
              fullWidth size="small" margin="dense" variant="outlined"
              value={form.name}
              onChange={f("name")}
              placeholder="Ex: Site Principal"
            />
            <TextField
              label="Título da janela de chat"
              fullWidth size="small" margin="dense" variant="outlined"
              value={form.title}
              onChange={f("title")}
              placeholder="Fale conosco"
            />
            <TextField
              label="Mensagem de boas-vindas (opcional)"
              fullWidth size="small" margin="dense" variant="outlined"
              value={form.welcomeMessage}
              onChange={f("welcomeMessage")}
              placeholder="Olá! Como posso ajudar?"
            />
            <TextField
              label="Domínios permitidos"
              fullWidth size="small" margin="dense" variant="outlined"
              value={form.allowedDomains}
              onChange={f("allowedDomains")}
              placeholder="* (todos) ou meusite.com,loja.com"
              helperText="Use * para qualquer site, ou liste domínios separados por vírgula"
            />

            <Box className={classes.colorRow} mt={1}>
              <FormControl size="small" variant="outlined" style={{ minWidth: 170 }}>
                <InputLabel>Posição da bolha</InputLabel>
                <Select
                  value={form.buttonPosition}
                  onChange={f("buttonPosition")}
                  label="Posição da bolha"
                >
                  <MenuItem value="bottom-right">Direita inferior</MenuItem>
                  <MenuItem value="bottom-left">Esquerda inferior</MenuItem>
                </Select>
              </FormControl>

              <Box className={classes.colorBlock}>
                <Typography variant="caption">Cor da bolha:</Typography>
                <Tooltip title="Clique para escolher a cor">
                  <input
                    type="color"
                    value={form.buttonColor}
                    onChange={f("buttonColor")}
                    className={classes.colorSwatch}
                  />
                </Tooltip>
              </Box>

              <Box className={classes.colorBlock}>
                <Typography variant="caption">Cor do chat:</Typography>
                <Tooltip title="Clique para escolher a cor">
                  <input
                    type="color"
                    value={form.primaryColor}
                    onChange={f("primaryColor")}
                    className={classes.colorSwatch}
                  />
                </Tooltip>
              </Box>
            </Box>

            {/* Snippet — só no modo edição */}
            {snippet && (
              <Box mt={3}>
                <Divider />
                <Typography variant="subtitle2" style={{ marginTop: 14, marginBottom: 6 }}>
                  Código para incorporar no site
                </Typography>
                <Paper className={classes.codeBox} onClick={handleCopy}>
                  {snippet}
                  <IconButton size="small" className={classes.copyBtn}>
                    <FileCopy style={{ fontSize: 14 }} />
                  </IconButton>
                </Paper>
                <Typography variant="caption" color="textSecondary">
                  Clique para copiar · Cole antes de &lt;/body&gt; do seu site
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={18} /> : isEdit ? "Salvar alterações" : "Criar widget"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatWidgetModal;
